import { ReactComponent as SvgTick } from '@img/icons/icon-tick-icon.svg';
import Button from '@stories/Components/Button/Button';
import React from 'react';
import { Controller, useForm, } from 'react-hook-form';
import { PuffLoader } from 'react-spinners';
import CheckBox from './FormControls/CheckBox/CheckBox';
import CheckBoxList from './FormControls/CheckBoxList/CheckBoxList';
import DropDownList from './FormControls/DropDownList/DropDownList';
import FileUpload from './FormControls/FileUpload/FileUpload';
import Hiddenfield from './FormControls/Hiddenfield/Hiddenfield';
import RadioButtonList from './FormControls/RadioButtonList/RadioButtonList';
import Recaptcha2 from './FormControls/Recaptcha2/Recaptcha2';
import Textarea from './FormControls/Textarea/Textarea';
import Textfield from './FormControls/Textfield/Textfield';
import TitleAndDescription from './FormControls/TitleAndDescription/TitleAndDescription';
import S from './UmbracoForm.styles';
import { FieldMode, useLoqate } from '@hooks/useLoqate';
const UmbracoForm = ({ formModel, submitText = 'Submit', submitUrl, }) => {
    const refContainer = React.useRef(null);
    const { control, formState: { errors, isSubmitting }, handleSubmit, register, watch, setValue, trigger, } = useForm();
    const values = watch();
    const [confirmationMessage, setConfirmationMessage] = React.useState('');
    const [serverErrors, setServerErrors] = React.useState([]);
    useLoqate({
        fields: [{ element: 'postcode', field: 'PostalCode', mode: FieldMode.SEARCH }],
        options: {},
        init: (control) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            control.listen('populate', (address) => {
                if (process.env.NODE_ENV === 'development') {
                    console.log('populate', address);
                }
                // Set value programmatically to ensure form state is updated.
                setValue('postcode', address.PostalCode);
                setValue('houseNameNumber', address.BuildingNumber || address.BuildingName || address.Company);
                setValue('addressLine1', address.Street);
                setValue('addressLine2', address.City);
                setValue('addressLine3', address.Province);
                trigger('postcode');
            });
        },
    });
    return (React.createElement(S.Container, { ref: refContainer },
        confirmationMessage && (React.createElement(S.ConfirmationMessage, null,
            React.createElement(SvgTick, null),
            React.createElement("span", { dangerouslySetInnerHTML: { __html: confirmationMessage } }))),
        !confirmationMessage && (React.createElement(S.Form, { noValidate: true, onSubmit: handleSubmit(onSubmitValid) },
            serverErrors.length > 0 && (React.createElement(S.ErrorList, null, serverErrors.map((error, index) => (React.createElement(S.ErrorListItem, { key: `error-${index}` }, error))))),
            React.createElement("input", { type: "hidden", value: formModel.id, ...register('formId', { required: true }) }),
            formModel.fieldSets.map((section) => (React.createElement(S.Fieldset, { key: section.title },
                section.title && React.createElement(S.Legend, null, section.title),
                section.fields.map((field) => (React.createElement(React.Fragment, { key: field.alias }, renderField(field, errors[field.alias]))))))),
            React.createElement(S.Submit, null,
                React.createElement(Button, { buttonType: "default", className: isSubmitting ? 'is-submitting' : '', disabled: isSubmitting, type: "submit", icon: isSubmitting ? React.createElement(PuffLoader, { color: "currentColor", size: 17 }) : undefined }, submitText))))));
    function renderField(fieldModel, error) {
        const registerOptions = {
            pattern: fieldModel.regex
                ? {
                    message: fieldModel.invalidErrorMessage
                        ? fieldModel.invalidErrorMessage
                        : 'Please enter a valid value.',
                    value: new RegExp(fieldModel.regex),
                }
                : undefined,
            required: {
                message: fieldModel.requiredErrorMessage
                    ? fieldModel.requiredErrorMessage
                    : 'Please enter a value.',
                value: fieldModel.required,
            },
        };
        const hasRules = fieldModel.rules.length;
        if (hasRules) {
            if ((fieldModel.actionType === 'show' && !rulesMatch(fieldModel, values)) || // If we are in show mode and the rules don't match
                (fieldModel.actionType === 'hide' && rulesMatch(fieldModel, values))) {
                // If we are in hide mode and the rules match
                return React.createElement(React.Fragment, null);
            }
        }
        switch (fieldModel.type) {
            case 'CheckBox':
                return (React.createElement(CheckBox, { error: error, field: fieldModel, ...register(fieldModel.alias, registerOptions) }));
            case 'CheckBoxList':
                return (React.createElement(CheckBoxList, { error: error, field: fieldModel, ...register(fieldModel.alias, registerOptions) }));
            case 'DropDownList':
                return (React.createElement(DropDownList, { error: error, field: fieldModel, ...register(fieldModel.alias, registerOptions) }));
            case 'FileUpload':
                return (React.createElement(FileUpload, { error: error, field: fieldModel, ...register(fieldModel.alias, registerOptions) }));
            case 'HiddenField':
                return (React.createElement(Hiddenfield, { error: error, field: fieldModel, ...register(fieldModel.alias, registerOptions) }));
            case 'RadioButtonList':
                return (React.createElement(RadioButtonList, { error: error, field: fieldModel, ...register(fieldModel.alias, registerOptions) }));
            case 'Recaptcha2':
                return (React.createElement(Controller, { control: control, defaultValue: "", name: fieldModel.alias, rules: registerOptions, render: ({ field }) => (React.createElement(Recaptcha2, { error: errors[fieldModel.alias], field: fieldModel, ...field })) }));
            case 'Textarea':
                return (React.createElement(Textarea, { error: error, field: fieldModel, ...register(fieldModel.alias, registerOptions) }));
            case 'Textfield':
                return (React.createElement(Textfield, { error: error, field: fieldModel, ...register(fieldModel.alias, registerOptions) }));
            case 'Email':
                return (React.createElement(Textfield, { error: error, field: fieldModel, type: "email", ...register(fieldModel.alias, registerOptions) }));
            case 'Password':
                return (React.createElement(Textfield, { error: error, field: fieldModel, type: "password", ...register(fieldModel.alias, registerOptions) }));
            case 'TitleAndDescription':
                return React.createElement(TitleAndDescription, { field: fieldModel });
            default:
                throw new Error(`Form Error: Unsupported field type ${fieldModel.type}.`);
        }
    }
    async function onSubmitValid(formState, event) {
        return postFormData(formState, event);
    }
    async function postFormData(formState, event) {
        const formData = new FormData(event?.target);
        const res = await fetch(submitUrl, {
            body: formData,
            method: 'POST',
        });
        const resData = (await res.json());
        if (process.env.NODE_ENV === 'development') {
            console.log(`Form: Response Data`, resData);
        }
        if (resData.errors?.length) {
            setServerErrors(resData.errors);
        }
        if (resData.message) {
            setConfirmationMessage(resData.message);
        }
        if (resData.redirect) {
            window.location.href = resData.redirect;
        }
    }
    function rulesMatch(fieldModel, values) {
        const matches = fieldModel.rules.map((rule) => {
            const stateValue = values[rule.field]?.toUpperCase();
            const rulesValue = rule.value?.toUpperCase();
            switch (rule.operator) {
                case 'Is':
                    return stateValue === rulesValue;
                case 'IsNot':
                    return stateValue !== rulesValue;
                case 'GreaterThan':
                    return parseInt(stateValue) > parseInt(rule.value);
                case 'LessThan':
                    return parseInt(stateValue) < parseInt(rule.value);
                case 'Contains':
                    return stateValue && stateValue.includes(rulesValue);
                case 'StartsWith':
                    return stateValue && stateValue.startsWith(rulesValue);
                case 'EndsWith':
                    return stateValue && stateValue.endsWith(rulesValue);
                default:
                    return false;
            }
        });
        if (fieldModel.logicType === 'all') {
            return matches.filter((match) => !match).length === 0;
        }
        else {
            return matches.filter((match) => match).length > 0;
        }
    }
};
export default UmbracoForm;
